import React, { useEffect, useRef, useState } from 'react';
import {
  HARDWARE_SCAN_IDLE_COMMIT_MS,
  commitIdleHardwareScan,
  consumeHardwareScannerKey,
  createHardwareScanState,
  hardwareCaptureAllowedTarget,
} from './hardwareScannerModel';
import { parseInventoryBarcodePayload } from './gs1BarcodeModel';
import './inventory-scanner.css';

const REQUESTED_FORMATS = [
  'qr_code',
  'data_matrix',
  'code_128',
  'code_39',
  'code_93',
  'codabar',
  'ean_13',
  'ean_8',
  'itf',
  'upc_a',
  'upc_e',
];

const HARDWARE_ARM_EVENT = 'line-medic-hardware-scanner-arm';
const CAMERA_LINEAR_FALLBACK_MS = 500;
const AUTO_SAVE_POLL_MS = 75;
const AUTO_SAVE_TIMEOUT_MS = 1800;
let hardwareInstanceSequence = 0;

function cleanScannerPayload(value) {
  return String(value ?? '').replace(/^[\r\n\t]+|[\r\n\t]+$/g, '').trim();
}

function cameraCodeRank(code) {
  const format = String(code?.format || '').toLowerCase();
  if (format === 'data_matrix') return 300;
  if (format === 'qr_code') return 200;
  return 100;
}

function bestCameraCode(codes) {
  const candidates = (Array.isArray(codes) ? codes : [])
    .filter(code => cleanScannerPayload(code?.rawValue));
  if (!candidates.length) return null;
  return [...candidates].sort((a, b) => {
    const rankDifference = cameraCodeRank(b) - cameraCodeRank(a);
    if (rankDifference) return rankDifference;
    return cleanScannerPayload(b.rawValue).length - cleanScannerPayload(a.rawValue).length;
  })[0] || null;
}

function cameraErrorMessage(err) {
  const name = String(err?.name || '');
  if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
    return 'Camera access was denied. Allow Camera for this site/app in iPhone Settings or Safari website settings, then try again.';
  }
  if (name === 'NotFoundError' || name === 'DevicesNotFoundError') return 'No usable camera was found on this device.';
  return err?.message || 'Unable to access the camera.';
}

function waitForVideo(ref) {
  return new Promise(resolve => {
    const check = () => {
      if (ref.current) resolve(ref.current);
      else requestAnimationFrame(check);
    };
    requestAnimationFrame(check);
  });
}

function parseExpirationInputDate(value) {
  const text = String(value || '').trim();
  if (!text) return null;
  let match = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(text);
  if (match) {
    const date = new Date(Number(match[3]), Number(match[1]) - 1, Number(match[2]), 12, 0, 0, 0);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  match = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(text);
  if (match) {
    const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12, 0, 0, 0);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  return null;
}

function autoSaveEditorForScan(primaryLookupKey) {
  if (!primaryLookupKey || typeof document === 'undefined') return null;
  if (document.querySelector('.inventory-scan-unmapped')) return null;
  const editor = document.querySelector('#inventory-item-editor');
  if (!editor || editor.classList.contains('inventory-counted')) return null;
  if (!String(editor.textContent || '').includes(primaryLookupKey)) return null;
  return editor;
}

function autoSaveReadyButton(editor) {
  if (!editor) return null;
  const expirationInput = [...editor.querySelectorAll('input')]
    .find(input => String(input.placeholder || '').includes('MM/YYYY or MM/DD/YYYY'));
  const noExpirationInput = editor.querySelector('.inventory-scan-na input[type="checkbox"]');
  const expirationValue = String(expirationInput?.value || '').trim();
  const expirationDate = parseExpirationInputDate(expirationValue);
  const today = new Date();
  today.setHours(12, 0, 0, 0);
  const expirationReady = Boolean(
    noExpirationInput?.checked
    || (expirationDate && expirationDate >= today)
  );
  if (!expirationReady) return null;
  return [...editor.querySelectorAll('.inventory-scan-actions button')]
    .find(button => String(button.textContent || '').includes('Save unit & scan next')) || null;
}

export default function InventoryBarcodeScanner({ onScan }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const frameRef = useRef(null);
  const zxingControlsRef = useRef(null);
  const cameraFallbackRef = useRef(null);
  const cameraFallbackStartedRef = useRef(0);
  const idleCommitRef = useRef(null);
  const autoSaveTimerRef = useRef(null);
  const hardwareBufferRef = useRef(createHardwareScanState());
  const instanceIdRef = useRef(`hardware-scanner-${++hardwareInstanceSequence}`);
  const [open, setOpen] = useState(false);
  const [manual, setManual] = useState('');
  const [error, setError] = useState('');
  const [hardwareArmed, setHardwareArmed] = useState(false);
  const [hardwarePaused, setHardwarePaused] = useState(false);
  const [keepHardwareReady, setKeepHardwareReady] = useState(true);
  const [lastHardwareScan, setLastHardwareScan] = useState('');
  const [cameraEngine, setCameraEngine] = useState('');

  const clearIdleCommit = () => {
    if (idleCommitRef.current) window.clearTimeout(idleCommitRef.current);
    idleCommitRef.current = null;
  };

  const clearAutoSaveTimer = () => {
    if (autoSaveTimerRef.current) window.clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = null;
  };

  const scheduleAutoSave = payload => {
    clearAutoSaveTimer();
    const parsed = parseInventoryBarcodePayload(payload);
    const primaryLookupKey = parsed?.primaryLookupKey || '';
    if (!primaryLookupKey) return;
    const startedAt = Date.now();

    const attempt = () => {
      autoSaveTimerRef.current = null;
      const editor = autoSaveEditorForScan(primaryLookupKey);
      const button = autoSaveReadyButton(editor);
      if (button && !button.disabled) {
        button.click();
        return;
      }
      if (Date.now() - startedAt < AUTO_SAVE_TIMEOUT_MS) {
        autoSaveTimerRef.current = window.setTimeout(attempt, AUTO_SAVE_POLL_MS);
      }
    };

    autoSaveTimerRef.current = window.setTimeout(attempt, 0);
  };

  const clearHardwareBuffer = () => {
    clearIdleCommit();
    hardwareBufferRef.current = createHardwareScanState();
  };

  const clearCameraFallback = () => {
    cameraFallbackRef.current = null;
    cameraFallbackStartedRef.current = 0;
  };

  const stopCamera = () => {
    if (frameRef.current) cancelAnimationFrame(frameRef.current);
    frameRef.current = null;
    if (zxingControlsRef.current) {
      try { zxingControlsRef.current.stop(); } catch (_) {}
      zxingControlsRef.current = null;
    }
    clearCameraFallback();
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setOpen(false);
    setCameraEngine('');
  };

  const disarmHardware = () => {
    clearHardwareBuffer();
    setHardwareArmed(false);
    setHardwarePaused(false);
  };

  useEffect(() => () => {
    stopCamera();
    clearHardwareBuffer();
    clearAutoSaveTimer();
  }, []);

  useEffect(() => {
    const handleOtherScannerArmed = event => {
      if (event?.detail?.id === instanceIdRef.current) return;
      disarmHardware();
    };
    window.addEventListener(HARDWARE_ARM_EVENT, handleOtherScannerArmed);
    return () => window.removeEventListener(HARDWARE_ARM_EVENT, handleOtherScannerArmed);
  }, []);

  const emitCameraOrManual = async value => {
    const payload = cleanScannerPayload(value);
    if (!payload) return;
    await onScan(payload);
    scheduleAutoSave(payload);
    setManual('');
    stopCamera();
  };

  const emitHardware = async value => {
    const payload = cleanScannerPayload(value);
    clearHardwareBuffer();
    if (!payload) return;
    setLastHardwareScan(payload);
    await onScan(payload);
    scheduleAutoSave(payload);
    if (!keepHardwareReady) setHardwareArmed(false);
  };

  useEffect(() => {
    if (!hardwareArmed) return undefined;

    const updatePaused = () => {
      setHardwarePaused(!hardwareCaptureAllowedTarget(document.activeElement));
    };

    const handleFocus = () => updatePaused();
    const handleKeyDown = event => {
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      if (!hardwareCaptureAllowedTarget(event.target)) return;

      const result = consumeHardwareScannerKey(hardwareBufferRef.current, event.key, Date.now());
      if (!result.consumed) return;

      hardwareBufferRef.current = result.state;
      event.preventDefault();
      event.stopPropagation();
      clearIdleCommit();

      if (result.barcode) {
        emitHardware(result.barcode);
        return;
      }

      if (result.terminal || !result.state.buffer) return;
      idleCommitRef.current = window.setTimeout(() => {
        const committed = commitIdleHardwareScan(hardwareBufferRef.current);
        hardwareBufferRef.current = committed.state;
        if (committed.barcode) emitHardware(committed.barcode);
      }, HARDWARE_SCAN_IDLE_COMMIT_MS);
    };

    updatePaused();
    window.addEventListener('focusin', handleFocus);
    window.addEventListener('focusout', handleFocus);
    window.addEventListener('keydown', handleKeyDown, true);
    return () => {
      window.removeEventListener('focusin', handleFocus);
      window.removeEventListener('focusout', handleFocus);
      window.removeEventListener('keydown', handleKeyDown, true);
      clearHardwareBuffer();
    };
  }, [hardwareArmed, keepHardwareReady, onScan]);

  const armHardware = () => {
    if (hardwareArmed) {
      disarmHardware();
      return;
    }
    stopCamera();
    setError('');
    setLastHardwareScan('');
    clearHardwareBuffer();
    setHardwareArmed(true);
    window.dispatchEvent(new CustomEvent(HARDWARE_ARM_EVENT, { detail: { id: instanceIdRef.current } }));
  };

  const startNativeCamera = async video => {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: {
        facingMode: { ideal: 'environment' },
        width: { ideal: 1280 },
        height: { ideal: 720 },
      },
      audio: false,
    });
    streamRef.current = stream;
    video.srcObject = stream;
    await video.play();

    let formats = REQUESTED_FORMATS;
    if (typeof window.BarcodeDetector.getSupportedFormats === 'function') {
      try {
        const supported = await window.BarcodeDetector.getSupportedFormats();
        formats = REQUESTED_FORMATS.filter(format => supported.includes(format));
      } catch (_) {
        formats = REQUESTED_FORMATS;
      }
    }

    const detector = formats.length
      ? new window.BarcodeDetector({ formats })
      : new window.BarcodeDetector();
    setCameraEngine('native');

    const scanFrame = async () => {
      try {
        if (videoRef.current?.readyState >= 2) {
          const codes = await detector.detect(videoRef.current);
          const best = bestCameraCode(codes);
          if (best?.rawValue) {
            const format = String(best.format || '').toLowerCase();
            if (format === 'data_matrix' || format === 'qr_code') {
              emitCameraOrManual(best.rawValue);
              return;
            }

            const payload = cleanScannerPayload(best.rawValue);
            if (!cameraFallbackRef.current || payload.length > cleanScannerPayload(cameraFallbackRef.current.rawValue).length) {
              cameraFallbackRef.current = best;
              cameraFallbackStartedRef.current = Date.now();
            }
          }

          if (
            cameraFallbackRef.current
            && cameraFallbackStartedRef.current
            && Date.now() - cameraFallbackStartedRef.current >= CAMERA_LINEAR_FALLBACK_MS
          ) {
            emitCameraOrManual(cameraFallbackRef.current.rawValue);
            return;
          }
        }
      } catch (_) {
        // Transient detector errors can happen while the camera is focusing.
      }
      frameRef.current = requestAnimationFrame(scanFrame);
    };

    frameRef.current = requestAnimationFrame(scanFrame);
  };

  const startZxingCamera = async video => {
    const { BrowserMultiFormatReader } = await import('@zxing/browser');
    const reader = new BrowserMultiFormatReader();
    setCameraEngine('zxing');
    zxingControlsRef.current = await reader.decodeFromConstraints(
      {
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false,
      },
      video,
      result => {
        const payload = result?.getText?.() || result?.text || '';
        if (payload) emitCameraOrManual(payload);
      },
    );
  };

  const start = async () => {
    disarmHardware();
    stopCamera();
    setError('');
    clearCameraFallback();
    setOpen(true);

    if (!navigator.mediaDevices?.getUserMedia) {
      setError('Camera access is not available in this browser. Type the barcode below or search the catalog.');
      return;
    }

    try {
      const video = await waitForVideo(videoRef);
      if ('BarcodeDetector' in window) await startNativeCamera(video);
      else await startZxingCamera(video);
    } catch (err) {
      stopCamera();
      setOpen(true);
      setError(cameraErrorMessage(err));
    }
  };

  return (
    <div className="inventory-scan-camera">
      <div className="inventory-scan-methods">
        <button type="button" className="inventory-scan-primary" onClick={open ? stopCamera : start}>
          {open ? 'Close phone camera' : 'Use phone camera'}
        </button>
        <button
          type="button"
          className={`inventory-scan-primary inventory-hardware-button${hardwareArmed ? ' active' : ''}`}
          onClick={armHardware}
        >
          {hardwareArmed ? 'Hardware scanner ready' : 'Use Bluetooth / USB scanner'}
        </button>
      </div>

      {hardwareArmed && (
        <div className={`inventory-hardware-status${hardwarePaused ? ' paused' : ''}`}>
          <div>
            <strong>{hardwarePaused ? 'Hardware scanner paused while typing' : 'Hardware scanner ready — scan now'}</strong>
            <span>
              {hardwarePaused
                ? 'Finish the field you are editing, then tap a button or blank area before scanning. This prevents a scanner from typing into lot, quantity, or expiration fields.'
                : 'Known individual units auto-save only when a valid, non-expired expiration is already available. Missing or expired dates stay open for review.'}
            </span>
          </div>
          <label className="inventory-hardware-keep">
            <input type="checkbox" checked={keepHardwareReady} onChange={event => setKeepHardwareReady(event.target.checked)} />
            Keep ready between scans
          </label>
        </div>
      )}

      {!hardwareArmed && lastHardwareScan && (
        <div className="inventory-scan-info">Hardware scan received: <strong>{lastHardwareScan}</strong></div>
      )}

      {open && (
        <div className="inventory-scan-camera-panel">
          {!error && <video ref={videoRef} playsInline muted />}
          {cameraEngine === 'zxing' && !error && <div className="inventory-scan-info">iPhone-compatible camera decoder active.</div>}
          {error && <div className="inventory-scan-warning">{error}</div>}
          <div className="inventory-scan-manual">
            <input
              value={manual}
              onChange={event => setManual(event.target.value)}
              placeholder="Or type barcode / UPC"
              autoCapitalize="off"
              autoCorrect="off"
              inputMode="text"
            />
            <button type="button" onClick={() => emitCameraOrManual(manual)} disabled={!manual.trim()}>Use</button>
          </div>
        </div>
      )}
    </div>
  );
}
